import { useEffect, useMemo, useState } from 'react';

export interface PromoMetrics {
  containerWidth: number;
  containerHeight: number;
  cardWidth: number;
  cardHeight: number;
  step: number;
  perspective: number;
  sideScale: number;
  centerZ: number;
  sideZ: number;
  rotateY: number;
}

// 1920px 视口下的参考设计（Figma 实测：中卡 x 830..1330 = 500 宽，banner 高 307 = 卡 280 + 23 + 点行 4）
// centerZ=0：设计稿中卡为平面矩形（无 3D 凸出），侧卡仍以 sideZ 后退 + rotateY 倾斜
// v1.4.101 sideZ -90 → -200：侧卡 rotateY 后朝外一边被透视放大（x 726 × 系数 1.012 ≈ 735 > 容器半宽 720），
// 探出约 15px 被容器 overflow:hidden 裁掉一条；后退加深后朝外边投影 ≈ 681，完整显示且纵深过渡更自然
// v1.4.102 step 495 → 536：侧卡外缘透视投影恰达容器半宽（(536+231)×0.938 ≈ 720），左右卡片
// 顶满容器两边；侧卡内缘投影 ≈ 250 与中卡边缘齐平，三卡视觉连排
// v1.4.103 step 536 → 500、sideZ -200 → -130：中卡遮挡侧卡约 20px（内缘投影 230 < 中卡半宽 250，
// z-index 中卡 10 > 侧卡 9）且外缘仍顶满容器（外缘投影 ≈ 720）；后退调浅使侧卡视觉变大，
// 内缘得以伸入中卡下方而不触发边缘裁切
// v1.4.104 step 500 → 565、sideZ -130 → -256：卡片间隙加大——侧卡内缘投影 230 → 265，与中卡
// 边缘（250）之间露出约 15px 缝隙（不再重叠）；后退加深使侧卡收窄，外缘投影 ≈ 720 仍顶满容器
// v1.4.105 step 565 → 517、sideZ -256 → -164：恢复中卡遮挡侧卡（内缘投影 ≈ 240，被中卡压住
// 约 10px，比 v1.4.103 的 20px 浅——间隙加大的同时保留中卡压边的层次），外缘投影 ≈ 720 仍顶满
const DESKTOP: PromoMetrics = {
  containerWidth: 1440,
  containerHeight: 280,
  cardWidth: 500,
  cardHeight: 280,
  step: 517,
  perspective: 1400,
  sideScale: 1.02,
  centerZ: 0,
  sideZ: -164,
  rotateY: 25,
};

function scaleMetrics(base: PromoMetrics, scale: number, containerWidth: number): PromoMetrics {
  const round = (n: number) => Math.round(n * scale);
  return {
    containerWidth,
    containerHeight: round(base.containerHeight),
    cardWidth: round(base.cardWidth),
    cardHeight: round(base.cardHeight),
    step: round(base.step),
    perspective: round(base.perspective),
    sideScale: base.sideScale,
    centerZ: round(base.centerZ),
    sideZ: round(base.sideZ),
    rotateY: base.rotateY,
  };
}

function getContentWidth(viewportWidth: number): number {
  // 与 HomeLayoutShell 保持一致：shell 总宽 = viewport ~ 1488px，内容区 = shell - 48px padding
  const shellWidth = Math.max(509, Math.min(viewportWidth, 1488));
  return shellWidth - 48;
}

function getMetrics(width: number): PromoMetrics {
  // 内容区宽度随窗口实时变化，范围 461px ~ 1440px
  const containerWidth = getContentWidth(width);
  return scaleMetrics(DESKTOP, containerWidth / 1440, containerWidth);
}

// 响应式：优先使用容器实测宽度（已扣除侧边栏与 shell padding），
// 而非 window.innerWidth —— 否则小屏 + 展开侧边栏时轮播按视口全宽布局，侧卡会被裁切错位
function getMetricsFromContent(contentWidth: number): PromoMetrics {
  const containerWidth = Math.round(Math.max(461, Math.min(contentWidth, 1440)));
  return scaleMetrics(DESKTOP, containerWidth / 1440, containerWidth);
}

export function usePromoMetrics(measuredContentWidth?: number | null): PromoMetrics {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );

  useEffect(() => {
    let raf = 0;
    const handleResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWidth(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return useMemo(() => {
    if (typeof measuredContentWidth === 'number' && measuredContentWidth > 0) {
      return getMetricsFromContent(measuredContentWidth);
    }
    return getMetrics(width);
  }, [measuredContentWidth, width]);
}
